<script setup lang="ts">
  import { AutoComplete, Button } from 'ant-design-vue'
  import { ColumnMapping } from '../models/ColumnMapping'
  import { reactive, watch } from 'vue'
  import { PlusOutlined } from '@ant-design/icons-vue'
  import { Trash2 } from 'lucide-vue-next'
  import { AvailableColumn } from '../models/AvailableColumn'
  import { useWindowSize } from '@/composables/useWindowSize'

  interface IColumnMappingRuleConfigurationProps {
    availableColumns: Array<AvailableColumn>
    modelValue: Array<ColumnMapping>
    placeHolder?: string
  }

  const props = defineProps<IColumnMappingRuleConfigurationProps>()

  const emit = defineEmits<{
    'update:modelValue': [value: Array<ColumnMapping>]
  }>()

  // Make a local reactive copy to work with
  const columnMappings = reactive<ColumnMapping[]>([...props.modelValue])

  watch(
    () => props.modelValue,
    (newVal) => {
      columnMappings.splice(0, columnMappings.length, ...newVal)
    },
    { deep: true }
  )

  watch(
    columnMappings,
    (newVal) => {
      emit('update:modelValue', [...newVal])
    },
    { deep: true }
  )

  const addPair = () => {
    columnMappings.push(new ColumnMapping())
  }

  const removePair = (index: number) => {
    columnMappings.splice(index, 1)
  }

  const { controlSize } = useWindowSize()
</script>

<template>
  <div class="column-mapping-selector">
    <div class="mt-[-40px] self-end">
      <Button
        type="primary"
        shape="circle"
        size="large"
        class="flex items-center justify-center"
        @click="addPair"
        :title="`Add Mapping`"
      >
        <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
      </Button>
    </div>

    <div class="mt-5">
      <div
        class="mapping-row flex items-center gap-2"
        v-for="(pair, index) in columnMappings"
        :key="index"
      >
        <AutoComplete
          v-model:value="pair.sourceColumn"
          :options="props.availableColumns"
          :placeholder="props.placeHolder || `Select or enter column`"
          :size="controlSize"
          class="w-full min-w-[100px]"
          allow-clear
        />

        <span>→</span>

        <AutoComplete
          v-model:value="pair.destinationColumn"
          :options="props.availableColumns"
          :placeholder="props.placeHolder || `Select or enter column`"
          :size="controlSize"
          class="w-full min-w-[100px]"
          allow-clear
        />

        <Button
          type="default"
          shape="circle"
          size="large"
          class="ant-btn-dangerous flex items-center justify-center bg-transparent"
          @click="removePair(index)"
          title="Remove Mapping"
        >
          <Trash2 class="text-base" style="line-height: 1; margin: 0" />
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .column-mapping-selector {
    margin-right: 10px;
    display: flex;
    flex-direction: column;
  }

  .mapping-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 15px;
  }

  select {
    padding: 4px;
  }

  .select-wrapper {
    overflow: hidden;
  }
</style>
